<template>
  <div class="wg-body">
    <!-- gate: never connected / disconnected -->
    <div v-if="!loading && !isConnected && !isExpired" class="wg-empty">
      <div class="eico"><i class="material-icons">link_off</i></div>
      <div class="et">Connect Search Console to see your Wins</div>
      <div class="es">
        Wins reports are measured from your Google Search Console data. Connect your account in
        Settings and we'll start tracking impressions, clicks and rankings automatically. Your
        earlier reports are kept and come back when you reconnect.
      </div>
      <button class="wg-pill solid gate-btn" @click="goToSettings('wg-gsc')">
        Connect in Settings <i class="material-icons arrow">arrow_forward</i>
      </button>
    </div>

    <template v-else-if="!loading">
      <!-- expired-access banner -->
      <div v-if="isExpired" class="wg-xbanner">
        <div class="xico"><i class="material-icons-outlined">warning</i></div>
        <div class="body">
          <div class="t">Search Console access expired</div>
          <div class="s">
            Google disconnected us (this happens when passwords change or access is revoked).
            Tracking is paused. The numbers below are from the last successful pull{{ lastPullLabel }}.
          </div>
        </div>
        <button class="wg-pill solid" @click="goToSettings('wg-gsc')">
          Reconnect <i class="material-icons arrow">arrow_forward</i>
        </button>
      </div>

      <div class="wg-wins-bar">
        <wins-period-picker
          :months="periods.months"
          :quarters="periods.quarters"
          :selected="selectedPeriod"
          @select="selectPeriod"
        />
        <wins-data-strip
          :freshness="snapshot ? snapshot.freshness : null"
          :frequency="frequencyAdverb"
          :checking="checkingData"
          :can-check="whiteGloveMissionControlAdminUi"
          :just-checked="justChecked"
          @check="checkForNewData"
        />
        <div class="sp"></div>
        <button
          v-if="whiteGloveMissionControlAdminUi"
          class="wg-gearbtn"
          title="Report settings"
          aria-label="Report settings"
          @click="showReportSettings = true"
        >
          <i class="material-icons-outlined">settings</i>
        </button>
      </div>

      <!-- period with no data yet -->
      <div v-if="!reportLoading && !hasReportData" class="wg-empty">
        <div class="eico"><i class="material-icons">trending_up</i></div>
        <div class="et">No Wins data for this period yet</div>
        <div class="es">
          We need a full {{ periodWord }} of Search Console history before we can compare
          performance. Your first report will appear here automatically once the period closes.
          Nothing for you to do.
        </div>
      </div>

      <template v-else-if="!reportLoading && snapshot">
        <wins-verdict-card
          :headline="snapshot.headline"
          :tier="snapshot.tier"
          :wins="snapshot.wins"
          :period-label="selectedPeriod ? selectedPeriod.label : ''"
          :period-word="periodWord"
          :provisional="snapshot.status === 'provisional'"
          :country="snapshot.countryLabel || ''"
          :country-full="snapshot.countryScopeFull || ''"
        />

        <wins-metric-cards :report="snapshot.report" @zoom="zoomedCard = $event" />

        <!-- new pages detected in Search Console -->
        <div v-if="pendingSuggestionsCount > 0" class="wg-gbanner">
          <div class="wg-gico-blue">G</div>
          <div class="body">
            <div class="t">
              {{ pendingSuggestionsCount }} new
              {{ pendingSuggestionsCount === 1 ? 'page' : 'pages' }} detected in Search Console
            </div>
            <div class="s">AI matched them to your keywords. Review and approve in Settings to start tracking.</div>
          </div>
          <button class="wg-pill solid" @click="goToSettings('wg-mapping')">
            Review in Settings <i class="material-icons arrow">arrow_forward</i>
          </button>
        </div>

        <wins-content-table
          :groups="snapshot.report.articleGroups"
          :waiting-on-url="snapshot.report.waitingOnUrl"
          :period-word="periodWord"
        />

        <!-- delivered articles missing URLs -->
        <div v-if="needsUrlCount > 0" class="wg-fbanner">
          <div class="fico"><i class="material-icons">link_off</i></div>
          <div class="body">
            <div class="t">
              {{ needsUrlCount }} delivered
              {{ needsUrlCount === 1 ? 'article needs a published URL' : 'articles need published URLs' }}
            </div>
            <div class="s">
              We can't measure an article until we know its live link. Add them and they'll appear
              here next time.
            </div>
          </div>
          <button class="wg-pill solid" @click="goToSettings('wg-urls')">
            Add URLs <i class="material-icons arrow">arrow_forward</i>
          </button>
        </div>

        <wins-position-distribution
          :now="snapshot.report.positionDistribution.now"
          :prev="snapshot.report.positionDistribution.prev"
          :prev-label="selectedPeriod ? selectedPeriod.prevLabel : ''"
          :period-word="periodWord"
        />

        <wins-past-reports :site-id="siteId" />

        <!-- next report footer -->
        <div class="wg-fbanner">
          <div class="body">
            <div class="t">Next {{ frequencyAdverb }} report: {{ nextReportLabel }} ({{ timezone }})</div>
            <div class="s">We pull Search Console data automatically. You'll receive the report via email.</div>
          </div>
          <button class="wg-pill outline" @click="showReportSettings = true">Email preferences</button>
        </div>
      </template>
    </template>

    <wins-chart-modal
      v-if="zoomedCard"
      :card="zoomedCard"
      :period-start="snapshot ? snapshot.periodStart : ''"
      :period-end="snapshot ? snapshot.periodEnd : ''"
      @close="zoomedCard = null"
    />

    <wins-report-settings-modal
      v-if="showReportSettings"
      :settings="settings"
      :can-edit="whiteGloveMissionControlAdminUi"
      :saving="savingReportSettings"
      :sending="sendingReport"
      @save="saveReportSettings"
      @send-now="sendReportNow"
      @close="showReportSettings = false"
    />

    <wg-toast ref="toast" />
  </div>
</template>

<script>
import moment from 'moment'
import WinsPeriodPicker from '@/components/SitesSingleView/WhiteGlove/Wins/WinsPeriodPicker.vue'
import WinsVerdictCard from '@/components/SitesSingleView/WhiteGlove/Wins/WinsVerdictCard.vue'
import WinsMetricCards from '@/components/SitesSingleView/WhiteGlove/Wins/WinsMetricCards.vue'
import WinsContentTable from '@/components/SitesSingleView/WhiteGlove/Wins/WinsContentTable.vue'
import WinsReportSettingsModal from '@/components/SitesSingleView/WhiteGlove/Wins/WinsReportSettingsModal.vue'
import WinsChartModal from '@/components/SitesSingleView/WhiteGlove/Wins/WinsChartModal.vue'
import WinsPastReports from '@/components/SitesSingleView/WhiteGlove/Wins/WinsPastReports.vue'
import WinsDataStrip from '@/components/SitesSingleView/WhiteGlove/Wins/WinsDataStrip.vue'
import WinsPositionDistribution from '@/components/SitesSingleView/WhiteGlove/Wins/WinsPositionDistribution.vue'
import WgToast from '@/components/SitesSingleView/WhiteGlove/Settings/WgToast.vue'

export default {
  name: 'WinsWhiteGlove',
  components: {
    WinsPeriodPicker,
    WinsVerdictCard,
    WinsMetricCards,
    WinsContentTable,
    WinsReportSettingsModal,
    WinsChartModal,
    WinsPastReports,
    WinsDataStrip,
    WinsPositionDistribution,
    WgToast,
  },
  provide () {
    return {
      /** Design-mock toast shared with the Settings tab: wgToast(message, action?, kind?) */
      wgToast: (message, action = null, kind = 'success') => {
        if (this.$refs.toast) this.$refs.toast.show(message, action, kind)
      },
    }
  },
  data () {
    return {
      loading: true,
      reportLoading: false,
      connection: null,
      pendingSuggestionsCount: 0,
      needsUrlCount: 0,
      settings: null,
      showReportSettings: false,
      savingReportSettings: false,
      sendingReport: false,
      checkingData: false,
      justChecked: false,
      nextReportDates: {},
      timezone: 'UTC',
      periods: { months: [], quarters: [] },
      selectedPeriod: null,
      snapshot: null,
      zoomedCard: null,
    }
  },
  computed: {
    whiteGloveMissionControlAdminUi () {
      const relation = this.$store.state.selectedSubAccountRelation
      return Boolean(relation && relation.isMissionControlSubAccount) &&
        this.$store.state.isWhiteGloveAdminMode
    },
    siteId () {
      const id = parseInt(this.$route.params.siteId, 10)
      return Number.isFinite(id) ? id : null
    },
    /** See Settings.vue: reload once a sub-account (mission control) relation is restored. */
    subAccountRelationId () {
      const r = this.$store.state.selectedSubAccountRelation
      return r && typeof r === 'object' && r.id != null ? r.id : null
    },
    loadKey () {
      return `${this.siteId}:${this.subAccountRelationId}`
    },
    isConnected () {
      return Boolean(this.connection) && this.connection.status === 'connected'
    },
    isExpired () {
      return Boolean(this.connection) && this.connection.status === 'expired'
    },
    lastPullLabel () {
      if (!this.connection || !this.connection.lastPullAt) return ''
      return ` on ${moment(this.connection.lastPullAt).format('MMM D, YYYY')}`
    },
    periodWord () {
      return this.selectedPeriod && this.selectedPeriod.kind === 'quarter' ? 'quarter' : 'month'
    },
    hasReportData () {
      return Boolean(this.snapshot && this.snapshot.report && this.snapshot.report.hasData)
    },
    frequencyAdverb () {
      return (this.settings && this.settings.frequency) || 'quarterly'
    },
    nextReportLabel () {
      const date = this.nextReportDates[this.frequencyAdverb]
      return date ? moment(date).format('MMMM D, YYYY') : '–'
    },
  },
  watch: {
    loadKey: {
      handler () {
        this.loadAll()
      },
      immediate: true,
    },
  },
  methods: {
    loadAll () {
      if (!this.siteId) return
      this.loading = true
      this.snapshot = null
      this.selectedPeriod = null
      this.$store.dispatch('getGscConnection', this.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.connection = response.data.connection || null
            this.pendingSuggestionsCount = response.data.pendingSuggestionsCount || 0
            this.needsUrlCount = response.data.needsUrlCount || 0
          }
          if (this.isConnected || this.isExpired) {
            // settings first: the default period depends on the frequency
            return this.loadSettings().then(() => this.loadPeriods())
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    checkForNewData () {
      if (this.checkingData) return
      this.checkingData = true
      this.$store.dispatch('checkWinsData', this.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.justChecked = true
            const days = (response.data.freshness && response.data.freshness.windowDays) || 90
            const word = this.frequencyAdverb === 'monthly' ? 'month' : 'quarter'
            this.toast(`Updated with Google's last ${days} days, compared to the ${word} before`)
            // refresh the period list (hasData flags) and re-select the
            // first period with data; the fresh pull already marked the
            // provisional snapshot stale, so the report recomputes on load
            return this.loadPeriods()
          }
          this.toast((response.data && response.data.msg) || 'Could not check for new data', null, 'error')
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.msg) ||
            'Could not check for new data'
          this.toast(msg, null, 'error')
        })
        .finally(() => {
          this.checkingData = false
        })
    },
    toast (message, action = null, kind = 'success') {
      if (this.$refs.toast) this.$refs.toast.show(message, action, kind)
    },
    sendReportNow () {
      if (this.sendingReport) return
      this.sendingReport = true
      // email the report the admin is looking at (rolling windows included)
      const period = this.selectedPeriod && this.selectedPeriod.key
      this.$store.dispatch('sendWinsReportNow', {
        siteId: this.siteId,
        body: period ? { period } : {},
      })
        .then((response) => {
          const data = response.data || {}
          if (data.status === 'SUCCESS') {
            this.toast(`${data.periodLabel} report sent to ${data.recipients}`)
          } else {
            this.toast(data.msg || 'Could not send the report', null, 'error')
          }
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.msg) ||
            'Could not send the report'
          this.toast(msg, null, 'error')
        })
        .finally(() => {
          this.sendingReport = false
        })
    },
    saveReportSettings (body) {
      this.savingReportSettings = true
      this.$store.dispatch('updateWinsReportSettings', { siteId: this.siteId, body })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.settings = response.data.settings
            this.nextReportDates = response.data.nextReportDates || {}
            this.timezone = response.data.timezone || 'UTC'
            this.showReportSettings = false
            this.toast('Report settings saved')
          } else {
            this.toast((response.data && response.data.msg) || 'Could not save the settings', null, 'error')
          }
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.msg) ||
            'Could not save the settings'
          this.toast(msg, null, 'error')
        })
        .finally(() => {
          this.savingReportSettings = false
        })
    },
    loadSettings () {
      return this.$store.dispatch('getWinsReportSettings', this.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.settings = response.data.settings
            this.nextReportDates = response.data.nextReportDates || {}
            this.timezone = response.data.timezone || 'UTC'
          }
        })
        .catch(() => {})
    },
    loadPeriods () {
      return this.$store.dispatch('listWinsPeriods', this.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.periods = {
              months: response.data.months || [],
              quarters: response.data.quarters || [],
            }
            // default to the report frequency's own list, where the rolling
            // live period (Last 30/90 days) sits first
            const quarterly = Boolean(this.settings && this.settings.frequency === 'quarterly')
            const ordered = quarterly
              ? [...this.periods.quarters, ...this.periods.months]
              : [...this.periods.months, ...this.periods.quarters]
            const firstWithData = ordered.find((p) => p.hasData)
            this.selectPeriod(firstWithData || ordered[0] || null)
          }
        })
        .catch(() => {})
    },
    selectPeriod (period) {
      this.selectedPeriod = period
      this.snapshot = null
      this.zoomedCard = null
      if (!period) return
      this.reportLoading = true
      this.$store.dispatch('getWinsReport', { siteId: this.siteId, period: period.key })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.snapshot = response.data.snapshot
          }
        })
        .catch(() => {})
        .finally(() => {
          this.reportLoading = false
        })
    },
    goToSettings (anchor) {
      this.$router.push({
        name: 'SettingsWhiteGlove',
        params: { siteId: this.$route.params.siteId },
        query: { anchor },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Settings/settings-card.scss';

/* tab body */
.wg-body {
  padding: 24px 0 32px;
}

/* empty state - EmptyUI.vue footer treatment (mock .wg-empty) */
.wg-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 13px;
  padding: 56px 20px;

  .eico {
    display: flex;
    align-items: center;
    justify-content: center;
    color: $wg-hairline;

    .material-icons {
      font-size: 40px;
    }
  }

  .et {
    font-size: 32px;
    font-weight: 800;
    color: $wg-hairline;
    line-height: 1.2;
    text-align: center;
  }

  .es {
    font-size: 0.9rem;
    line-height: 1.6;
    color: $pop-ink-muted;
    max-width: 560px;
    margin: 0 auto;
    text-align: center;
  }

  .gate-btn {
    margin-top: 16px;
  }
}

.wg-pill .arrow {
  font-size: 16px;
}

/* expired-access banner */
.wg-xbanner {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff6e5;
  border: 1px solid #ead9a8;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;

  .xico {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #f3e3b5;
    color: #8a6d1e;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;

    .material-icons-outlined {
      font-size: 1.2rem;
    }
  }

  .body {
    flex: 1;

    .t {
      font-weight: 800;
      font-size: 13.5px;
      color: $pop-ink;
    }

    .s {
      font-size: 12px;
      color: $pop-ink-muted;
      margin-top: 2px;
    }
  }
}

.wg-gearbtn {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid $wg-panel-line;
  background: #fff;
  color: $pop-ink-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
  flex: none;

  &:hover {
    color: $pop-teal;
    border-color: $pop-teal;
  }

  .material-icons-outlined {
    font-size: 20px;
  }
}

.wg-wins-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;

  .sp {
    flex: 1;
  }
}

/* GSC detected banner */
.wg-gbanner {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid $wg-panel-line;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 28px;

  .body {
    flex: 1;

    .t {
      font-size: 0.95rem;
      font-weight: 700;
      color: $pop-teal;
    }

    .s {
      font-size: 0.85rem;
      color: $pop-body;
      margin-top: 3px;
      line-height: 1.5;
    }
  }
}

.wg-gico-blue {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  background: $pop-link;
}

/* footer banners */
.wg-fbanner {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid $wg-panel-line;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 16px;

  .fico {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: $pop-accent;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;

    .material-icons,
    .material-icons-outlined {
      font-size: 1.35rem;
    }
  }

  .body {
    flex: 1;

    .t {
      font-size: 0.95rem;
      font-weight: 700;
      color: $pop-teal;
    }

    .s {
      font-size: 0.85rem;
      color: $pop-body;
      margin-top: 3px;
      line-height: 1.5;
    }
  }
}
</style>
